import type { ShiftType } from '@streep/types'

/** Standaard BTW-percentage (NL hoog tarief). */
export const DEFAULT_VAT_PERCENTAGE = 21

/** Standaard reisvergoeding per kilometer (€/km). */
export const DEFAULT_KM_RATE = 0.3

/**
 * Standaard ploegentoeslag-percentage per ShiftType.
 * Conform CAO Metaal & Techniek (business rules 25–26).
 */
export const SHIFT_SURCHARGE_DEFAULTS: Record<ShiftType, number> = {
  DAG: 0,
  PLOEGEN_2: 18,
  PLOEGEN_3: 26,
  PLOEGEN_4: 30,
  PLOEGEN_5: 33,
}

const round2 = (n: number): number => Math.round(n * 100) / 100

const isFiniteNumber = (n: unknown): n is number =>
  typeof n === 'number' && Number.isFinite(n)

/**
 * Bepaalt het ploegentoeslag-percentage voor een ShiftType.
 * Een expliciete `override` (klant-afspraak) wint van de CAO-default,
 * inclusief de waarde 0 (klant met afgekochte toeslag).
 */
export function getShiftSurchargePercentage(
  shiftType: ShiftType,
  override?: number | null,
): number {
  if (isFiniteNumber(override) && override >= 0) return override
  return SHIFT_SURCHARGE_DEFAULTS[shiftType]
}

/**
 * Berekent de ploegentoeslag in euro over een arbeidsbedrag.
 * Bij negatieve of nul-arbeid: 0.
 */
export function calculateShiftSurcharge(
  arbeidExclToeslag: number,
  shiftType: ShiftType,
  override?: number | null,
): number {
  if (!isFiniteNumber(arbeidExclToeslag) || arbeidExclToeslag <= 0) return 0
  const pct = getShiftSurchargePercentage(shiftType, override)
  return round2((arbeidExclToeslag * pct) / 100)
}

/**
 * Berekent de reisvergoeding voor N kilometers.
 * Default tarief is €0,30/km; klant-override (bv. €0,45) wint.
 * Negatieve km worden als 0 behandeld.
 */
export function calculateKmReimbursement(
  km: number,
  ratePerKm?: number | null,
): number {
  if (!isFiniteNumber(km) || km <= 0) return 0
  const rate = isFiniteNumber(ratePerKm) && ratePerKm >= 0 ? ratePerKm : DEFAULT_KM_RATE
  return round2(km * rate)
}

/**
 * Berekent het BTW-bedrag op een subtotaal.
 * Default 21%; per factuur kan een ander percentage gelden (laag tarief, EU-export).
 */
export function calculateVatAmount(
  subtotal: number,
  vatPercentage: number = DEFAULT_VAT_PERCENTAGE,
): number {
  if (!isFiniteNumber(subtotal) || subtotal <= 0) return 0
  return round2((subtotal * vatPercentage) / 100)
}

export interface InvoiceLineForTotal {
  total: number
}

export interface InvoiceTotals {
  subtotal: number
  vatAmount: number
  total: number
}

/**
 * Som van regel-totalen → subtotaal, met BTW toegevoegd.
 * Alle bedragen op 2 decimalen.
 */
export function calculateInvoiceTotals(
  lines: InvoiceLineForTotal[],
  vatPercentage: number = DEFAULT_VAT_PERCENTAGE,
): InvoiceTotals {
  const rawSubtotal = lines.reduce((s, l) => s + (isFiniteNumber(l.total) ? l.total : 0), 0)
  const subtotal = round2(rawSubtotal)
  const vatAmount = calculateVatAmount(subtotal, vatPercentage)
  const total = round2(subtotal + vatAmount)
  return { subtotal, vatAmount, total }
}
